<!--
/**
 * FormKitCampaignSelect
 *
 * Salesforce campaign autocomplete with server-side search on name or exact Salesforce ID.
 * Each option shows its record type, status and start date, and campaigns already linked to
 * another event are shown but can't be picked.
 *
 * FormKit usage:
 *   <FormKit type="campaignSelect" name="campaignId" :current-event-id="event.id" />
 *
 * @prop {string} [currentEventId] - The event being edited, so its own campaign stays selectable.
 */
-->
<template>
  <div v-bind="attrs" :aria-required="isRequired" :class="wrapperClass">
    <AutoComplete
      :input-id="inputId"
      :model-value="displayValue"
      :suggestions="suggestions"
      option-label="label"
      option-disabled="disabled"
      :placeholder="displayPlaceholder"
      :disabled="disabled"
      :show-clear="true"
      :delay="300"
      complete-on-focus
      class="w-full"
      data-testid="formkit-campaign-select-input"
      @complete="onSearch"
      @update:model-value="onSelect"
    >
      <template #option="{ option }">
        <div class="flex flex-col">
          <span>{{ option.label }}</span>
          <span class="text-xs text-fp-gray-5">{{ option.detail }}</span>
        </div>
      </template>
    </AutoComplete>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import type { FormKitFrameworkContext } from '@formkit/core'
import AutoComplete from 'primevue/autocomplete'
import type { EventCampaign } from '~/models/graphql/generated/types'
import {
  useFormKitAutoComplete,
  type AutoCompleteOption,
} from '~/composables/useFormKitAutoComplete'
import { dateText } from '~/services/dateService'

export type CampaignOption = AutoCompleteOption & {
  detail: string
  disabled: boolean
}

const props = defineProps({
  context: {
    type: Object as PropType<
      FormKitFrameworkContext & {
        placeholder?: string
        disabled?: boolean
        currentEventId?: string
      }
    >,
    default: undefined,
  },
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  currentEventId: { type: String, default: undefined },
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const bffClient = useBffClient()

function optionFromCampaign(campaign: EventCampaign): CampaignOption {
  const currentEventId = props.context?.currentEventId ?? props.currentEventId
  const linkedElsewhere =
    campaign.linkedEventId != null &&
    String(campaign.linkedEventId) !== String(currentEventId)

  const detail = [
    campaign.recordTypeName,
    campaign.status,
    campaign.startDate ? dateText(campaign.startDate) : null,
    campaign.active ? null : 'Inactive',
    linkedElsewhere ? `Linked to ${campaign.linkedEventTitle}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return {
    id: String(campaign.id),
    label: campaign.name,
    detail,
    disabled: linkedElsewhere,
  }
}

async function search(query: string): Promise<CampaignOption[]> {
  try {
    const campaigns = await bffClient.getCampaigns({
      search: query?.trim() || null,
    })
    return campaigns.map(optionFromCampaign)
  } catch {
    return []
  }
}

async function loadById(id: string): Promise<CampaignOption | null> {
  try {
    const [campaign] = await bffClient.getCampaigns({ ids: [id] })
    if (campaign) return optionFromCampaign(campaign)
  } catch {
    // Leave display unchanged so existing label can remain
  }
  return null
}

const {
  suggestions,
  displayValue,
  displayPlaceholder,
  disabled,
  inputId,
  isRequired,
  attrs,
  onSearch,
  onSelect,
  wrapperClass,
} = useFormKitAutoComplete({ props, emit, search, loadById })
</script>
